import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { getAPICall, postAPICall } from "../../../config/apiCalls";
import { Box, IconButton, InputAdornment, LinearProgress, TextField, Typography } from "@mui/material";
import { Clear as ClearIcon, Description as DescriptionIcon } from "@mui/icons-material";
import "../../DropboxInterrogator/DropboxInterrogator.css";

// ── Helpers (mirrored from DropboxInterrogator) ───────────────────────────────

const FILTER_KEY = "dropbox_tree_filter";
const FILE_VIS_KEY = "dropbox_tree_file_visibility";

const initialTypeFilters = {
	all: true,
	doc: false,
	xls: false,
	pdf: false,
	jpg: false,
	pst: false,
	mov: false,
};

const typeLabelMap = {
	doc: ".docx/.doc",
	xls: ".xlsx/.xls",
	pdf: ".pdf",
	jpg: ".jpg/.jpeg",
	pst: ".pst",
	mov: ".mov/.mp4",
};

const normalizePath = (path) => {
	if (!path) return "";
	let normalized = String(path).trim();
	if (!normalized) return "";
	normalized = normalized.startsWith("/") ? normalized : `/${normalized}`;
	normalized = normalized.replace(/\/{2,}/g, "/");
	if (normalized.length > 1) normalized = normalized.replace(/\/+$/g, "");
	return normalized;
};

const pathBasename = (path) => {
	const parts = normalizePath(path).split("/").filter(Boolean);
	return parts.length ? parts[parts.length - 1] : "/";
};

const toDropboxApiPath = (path) => {
	const normalized = normalizePath(path);
	return normalized === "/" ? "" : normalized;
};

const classifyFile = (name) => {
	const lower = (name || "").toLowerCase();
	if (lower.endsWith(".docx") || lower.endsWith(".doc")) return "doc";
	if (lower.endsWith(".xlsx") || lower.endsWith(".xls")) return "xls";
	if (lower.endsWith(".pdf")) return "pdf";
	if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "jpg";
	if (lower.endsWith(".pst")) return "pst";
	if (lower.endsWith(".mov") || lower.endsWith(".mp4")) return "mov";
	return "other";
};

const countTypes = (entries) => {
	const counts = {
		doc: { count: 0, bytes: 0 },
		xls: { count: 0, bytes: 0 },
		pdf: { count: 0, bytes: 0 },
		jpg: { count: 0, bytes: 0 },
		pst: { count: 0, bytes: 0 },
		mov: { count: 0, bytes: 0 },
	};
	entries.forEach((entry) => {
		if (entry[".tag"] !== "file") return;
		const kind = classifyFile(entry.name);
		const size = typeof entry.size === "number" ? entry.size : 0;
		if (counts[kind]) {
			counts[kind].count += 1;
			counts[kind].bytes += size;
		}
	});
	return counts;
};

const formatBytes = (bytes) => {
	if (!bytes || bytes <= 0) return "0B";
	const units = ["B", "KB", "MB", "GB", "TB"];
	let val = bytes;
	let idx = 0;
	while (val >= 1024 && idx < units.length - 1) {
		val /= 1024;
		idx += 1;
	}
	const rounded = val >= 10 || idx === 0 ? val.toFixed(0) : val.toFixed(1);
	return `${rounded}${units[idx]}`;
};

const buildDropboxFolderUrl = (path) => {
	if (!path) return "https://www.dropbox.com/home";
	const encoded = normalizePath(path)
		.split("/")
		.map((s) => encodeURIComponent(s))
		.join("/");
	return `https://www.dropbox.com/home${encoded}`;
};

const buildDropboxPreviewUrl = (path) => {
	if (!path) return "https://www.dropbox.com/home";
	const encoded = normalizePath(path)
		.split("/")
		.map((s) => encodeURIComponent(s))
		.join("/");
	return `https://www.dropbox.com/preview${encoded}`;
};

// ── Session-scoped tree cache (persists across tab switches, keyed by path) ───

const _treeCache = new Map();

// ── Component ─────────────────────────────────────────────────────────────────

const DropboxFolderView = ({ folderPath, reportingWindowStart, reportingWindowEnd, reportingPeriodDisplay, filterState, onFilterChange }) => {
	const { instance, accounts } = useMsal();

	const [treeRows, setTreeRows] = useState([]);
	const [nameMap, setNameMap] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [loadError, setLoadError] = useState("");
	const [progress, setProgress] = useState({ processed: 0, queued: 0, currentPath: "/", dots: "" });

	// When filterState/onFilterChange are provided, the parent controls all filter state.
	// Otherwise fall back to internal localStorage-backed state (existing behaviour).
	const controlled = filterState !== undefined && onFilterChange !== undefined;

	const [_folderFilter, _setFolderFilter] = useState(() => {
		const saved = localStorage.getItem(FILTER_KEY);
		return saved === "empty" || saved === "not_empty" ? saved : "all";
	});
	const [_fileVisibility, _setFileVisibility] = useState(() => {
		const saved = localStorage.getItem(FILE_VIS_KEY);
		return saved === "show" ? "show" : "hide";
	});
	const [_typeFilters, _setTypeFilters] = useState(initialTypeFilters);
	const [_fileNameFilter, _setFileNameFilter] = useState("");
	const [_filtersOpen, _setFiltersOpen] = useState(false); // eslint-disable-line no-unused-vars
	const [_viewMode, _setViewMode] = useState("all");

	const folderFilter    = controlled ? filterState.folderFilter    : _folderFilter;
	const fileVisibility  = controlled ? filterState.fileVisibility  : _fileVisibility;
	const typeFilters     = controlled ? filterState.typeFilters     : _typeFilters;
	const fileNameFilter  = controlled ? filterState.fileNameFilter  : _fileNameFilter;
	// eslint-disable-next-line no-unused-vars
	const filtersOpen     = controlled ? filterState.filtersOpen     : _filtersOpen;
	const viewMode        = controlled ? filterState.viewMode        : _viewMode;

	const makeControlledSetter = (key, currentValue) => (v) =>
		onFilterChange({ ...filterState, [key]: typeof v === "function" ? v(currentValue) : v });

	const setFolderFilter   = controlled ? makeControlledSetter("folderFilter",   folderFilter)   : _setFolderFilter;
	const setFileVisibility = controlled ? makeControlledSetter("fileVisibility", fileVisibility) : _setFileVisibility;
	const setTypeFilters    = controlled ? makeControlledSetter("typeFilters",    typeFilters)    : _setTypeFilters;
	const setFileNameFilter = controlled ? makeControlledSetter("fileNameFilter", fileNameFilter) : _setFileNameFilter;
	const setFiltersOpen    = controlled ? makeControlledSetter("filtersOpen",    filtersOpen)    : _setFiltersOpen; // eslint-disable-line no-unused-vars
	const setViewMode       = controlled ? makeControlledSetter("viewMode",       viewMode)       : _setViewMode;

	const cancelRef = useRef({ cancelRequested: false });
	const heartbeatRef = useRef(null);

	useEffect(() => { if (!controlled) localStorage.setItem(FILTER_KEY, folderFilter); }, [folderFilter, controlled]);
	useEffect(() => { if (!controlled) localStorage.setItem(FILE_VIS_KEY, fileVisibility); }, [fileVisibility, controlled]);

	const listFolder = useCallback(async (path) => {
		const dropboxPath = toDropboxApiPath(normalizePath(path));
		const response = await getAPICall(instance, accounts[0], "/api/dropbox/listFolders", { path: dropboxPath });
		const data = response?.data;
		if (!data || !Array.isArray(data.entries)) {
			throw new Error(data?.error_summary || data?.error_description || data?.message || "Unable to list folder contents.");
		}
		if (data.error || data.error_summary) {
			throw new Error(data.error_summary || data.error_description || "Unable to list folder contents.");
		}
		return data;
	}, [instance, accounts]);

	const buildTree = useCallback(async (rootPath) => {
		const rows = [];
		let heartbeatTick = 0;
		let processed = 0;

		const stack = [{ path: normalizePath(rootPath), label: rootPath ? pathBasename(rootPath) : "/", depth: 0 }];

		heartbeatRef.current = setInterval(() => {
			heartbeatTick += 1;
			setProgress((prev) => ({ ...prev, dots: ".".repeat((heartbeatTick % 3) + 1) }));
		}, 500);

		while (stack.length) {
			if (cancelRef.current.cancelRequested) {
				return { rows, cancelled: true };
			}

			const current = stack.pop();
			setProgress({ processed, queued: stack.length, currentPath: current.path || "/", dots: "." });

			let data;
			try {
				data = await listFolder(current.path);
			} catch (err) {
				processed += 1;
				setProgress({ processed, queued: stack.length, currentPath: current.path || "/", dots: "." });
				continue;
			}
			const entries = data.entries || [];
			const folders = entries.filter((e) => e[".tag"] === "folder");
			const files = entries.filter((e) => e[".tag"] === "file");
			const typeCounts = countTypes(entries);
			const folderBytes = files.reduce((sum, f) => sum + (typeof f.size === "number" ? f.size : 0), 0);

			rows.push({
				label: current.label,
				depth: current.depth,
				fileCount: files.length,
				totalBytes: folderBytes,
				typeCounts,
				fullPath: current.path || rootPath || "",
				fileNames: files.map((f) => ({
					name: f.name || "",
					kind: classifyFile(f.name || ""),
					bytes: typeof f.size === "number" ? f.size : 0,
					fullPath: f.path_display || (current.path ? `${current.path}/${f.name || ""}` : `/${f.name || ""}`),
					modified: f.server_modified || "",
					modifiedById: f.sharing_info?.modified_by || "",
				})),
			});

			const sortedFolders = folders.slice().sort((a, b) =>
				(a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" })
			);
			for (let i = sortedFolders.length - 1; i >= 0; i -= 1) {
				const folder = sortedFolders[i];
				const childPath = folder.path_display || folder.path_lower || "";
				stack.push({ path: childPath, label: folder.name || pathBasename(childPath), depth: current.depth + 1 });
			}

			processed += 1;
			setProgress({ processed, queued: stack.length, currentPath: current.path || "/", dots: "." });
		}

		return { rows, cancelled: false };
	}, [listFolder]);

	useEffect(() => {
		if (!folderPath) {
			setTreeRows([]);
			setNameMap({});
			setLoadError("");
			return;
		}

		const cached = _treeCache.get(folderPath);
		if (cached) {
			setTreeRows(cached);
			setIsLoading(false);
			return;
		}

		cancelRef.current.cancelRequested = false;
		setIsLoading(true);
		setLoadError("");
		setTreeRows([]);
		setNameMap({});

		buildTree(folderPath)
			.then(async (result) => {
				if (cancelRef.current.cancelRequested) return;
				const rows = result.rows;
				_treeCache.set(folderPath, rows);
				setTreeRows(rows);
			})
			.catch((err) => {
				if (!cancelRef.current.cancelRequested) setLoadError(err?.message || "Failed to analyze folder.");
			})
			.finally(() => {
				if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
				if (!cancelRef.current.cancelRequested) setIsLoading(false);
			});

		return () => {
			cancelRef.current.cancelRequested = true;
			if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; }
		};
	}, [folderPath, buildTree, instance, accounts]);

	// ── Filter helpers ────────────────────────────────────────────────────────

	const getSelectedTypes = () => {
		if (typeFilters.all) return ["all"];
		return Object.keys(typeFilters).filter((k) => k !== "all" && typeFilters[k]);
	};

	const filteredFiles = (row, selectedTypes, normalizedNameFilter, periodFilter = null) => {
		if (!row.fileNames?.length) return [];
		return row.fileNames.filter((file) => {
			const typeMatch = selectedTypes.includes("all") || selectedTypes.includes(file.kind);
			const nameMatch = !normalizedNameFilter || file.name.toLowerCase().includes(normalizedNameFilter);
			const periodMatch = !periodFilter || (
				file.modified &&
				file.modified >= periodFilter.start &&
				file.modified < periodFilter.end
			);
			return typeMatch && nameMatch && periodMatch;
		});
	};

	const countSelected = (row, selectedTypes, normalizedNameFilter, periodFilter = null) => {
		if (selectedTypes.includes("all") && !normalizedNameFilter && !periodFilter) return row.fileCount;
		return filteredFiles(row, selectedTypes, normalizedNameFilter, periodFilter).length;
	};

	const selectedBytes = (row, selectedTypes, normalizedNameFilter, periodFilter = null) => {
		if (selectedTypes.includes("all") && !normalizedNameFilter && !periodFilter) return row.totalBytes;
		return filteredFiles(row, selectedTypes, normalizedNameFilter, periodFilter).reduce((sum, f) => sum + (f.bytes || 0), 0);
	};

	const selectedTypeSummary = (row, selectedTypes, normalizedNameFilter, periodFilter = null) => {
		const count = countSelected(row, selectedTypes, normalizedNameFilter, periodFilter);
		const bytesLabel = formatBytes(selectedBytes(row, selectedTypes, normalizedNameFilter, periodFilter));
		if (selectedTypes.includes("all") || normalizedNameFilter || periodFilter) return `${count} files, ${bytesLabel}`;
		const parts = selectedTypes.map((type) => `${row.typeCounts[type]?.count || 0} ${typeLabelMap[type]}`);
		return `${parts.join(", ") || `${count} files`}, ${bytesLabel}`;
	};

	const filteredTreeRows = useMemo(() => {
		const selectedTypes = getSelectedTypes();
		const normalizedNameFilter = fileNameFilter.trim().toLowerCase();
		const periodFilter = viewMode === "period" && reportingWindowStart && reportingWindowEnd
			? { start: reportingWindowStart, end: reportingWindowEnd + "T23:59:59.999Z" }
			: null;
		return treeRows
			.filter((row) => {
				const count = countSelected(row, selectedTypes, normalizedNameFilter, periodFilter);
				if (periodFilter && count === 0) return false;
				if (folderFilter === "empty") return count === 0;
				if (folderFilter === "not_empty") return count > 0;
				return true;
			})
			.filter((row) => !normalizedNameFilter || countSelected(row, selectedTypes, normalizedNameFilter, periodFilter) > 0)
			.map((row) => {
				const files = filteredFiles(row, selectedTypes, normalizedNameFilter, periodFilter)
					.slice()
					.sort((a, b) => periodFilter
						? (b.modified || "").localeCompare(a.modified || "")
						: a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
					);
				return {
					...row,
					selectedCount: countSelected(row, selectedTypes, normalizedNameFilter, periodFilter),
					selectedBytes: selectedBytes(row, selectedTypes, normalizedNameFilter, periodFilter),
					displaySummary: selectedTypeSummary(row, selectedTypes, normalizedNameFilter, periodFilter),
					files,
				};
			});
	}, [treeRows, folderFilter, typeFilters, fileNameFilter, viewMode, reportingWindowStart, reportingWindowEnd]);

	const summaryLabel = useMemo(() => {
		if (!treeRows.length) return "";
		const totalFiles = filteredTreeRows.reduce((sum, row) => sum + row.selectedCount, 0);
		const totalBytes = filteredTreeRows.reduce((sum, row) => sum + row.selectedBytes, 0);
		return `${totalFiles} files, ${formatBytes(totalBytes)} total`;
	}, [treeRows, filteredTreeRows]);

	const progressPercent = useMemo(() => {
		if (!isLoading) return 0;
		const total = progress.processed + progress.queued + 1;
		return Math.min(100, Math.round((progress.processed / total) * 100));
	}, [isLoading, progress.processed, progress.queued]);

	const handleTypeAllChange = (checked) => {
		if (checked) { setTypeFilters(initialTypeFilters); return; }
		setTypeFilters((prev) => ({ ...prev, all: false }));
	};

	const handleTypeChange = (key, checked) => {
		setTypeFilters((prev) => {
			const next = { ...prev, [key]: checked };
			const hasSpecific = Object.entries(next).some(([k, v]) => k !== "all" && v);
			next.all = !hasSpecific;
			return next;
		});
	};

	// ── Render ────────────────────────────────────────────────────────────────

	if (!folderPath) return null;

	return (
		<div className="dropboxFolderView">
			<div className="projectViewerInfoBox">
				<span className="projectViewerInfoBoxLabel">Info:</span>
				<span>To view a more detailed breakdown of changes in the past reporting period please refer to the weekly report document.</span>
			</div>
			<div style={{ marginBottom: "10px", display: "flex", flexDirection: "column", gap: "8px" }}>
				<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
					<TextField
						size="small"
						placeholder="Filter by file name…"
						value={fileNameFilter}
						onChange={(e) => setFileNameFilter(e.target.value)}
						style={{ flex: 1 }}
						InputProps={{
							endAdornment: fileNameFilter ? (
								<InputAdornment position="end">
									<IconButton size="small" onClick={() => setFileNameFilter("")} edge="end">
										<ClearIcon fontSize="small" />
									</IconButton>
								</InputAdornment>
							) : null,
						}}
					/>
					{summaryLabel && <span className="dropboxFolderViewSummary">{summaryLabel}</span>}
				</div>
				<div className="projectViewerTableFilters" style={{ flexWrap: "wrap", rowGap: "8px" }}>
					<div className="projectViewerMiniTabs" role="group" aria-label="Folder visibility">
						{[["all", "All Folders"], ["not_empty", "Has Files"], ["empty", "Empty"]].map(([val, label]) => (
							<button key={val} type="button" className={`projectViewerMiniTab${folderFilter === val ? " active" : ""}`} onClick={() => setFolderFilter(val)}>{label}</button>
						))}
					</div>
					<div className="projectViewerMiniTabs" role="group" aria-label="Files modified">
						<button type="button" className={`projectViewerMiniTab${viewMode === "all" ? " active" : ""}`} onClick={() => setViewMode("all")}>All Time</button>
						<button
							type="button"
							className={`projectViewerMiniTab${viewMode === "period" ? " active" : ""}`}
							onClick={() => setViewMode("period")}
							disabled={!reportingWindowStart || !reportingWindowEnd}
							title={reportingPeriodDisplay || "No report period available"}
						>
							{reportingPeriodDisplay ? `Report Period (${reportingPeriodDisplay})` : "Report Period"}
						</button>
					</div>
					<div className="projectViewerMiniTabs" role="group" aria-label="File types">
						<button type="button" className={`projectViewerMiniTab${typeFilters.all ? " active" : ""}`} onClick={() => handleTypeAllChange(true)}>All</button>
						{Object.entries({ doc: ".doc", xls: ".xls", pdf: ".pdf", jpg: ".jpg", pst: ".pst", mov: ".mov" }).map(([key, label]) => (
							<button key={key} type="button" className={`projectViewerMiniTab${typeFilters[key] ? " active" : ""}`} onClick={() => handleTypeChange(key, !typeFilters[key])}>{label}</button>
						))}
					</div>
					<div className="projectViewerMiniTabs" role="group" aria-label="File visibility">
						{[["hide", "Hide Files"], ["show", "Show Files"]].map(([val, label]) => (
							<button key={val} type="button" className={`projectViewerMiniTab${fileVisibility === val ? " active" : ""}`} onClick={() => setFileVisibility(val)}>{label}</button>
						))}
					</div>
				</div>
			</div>

			<div className="dropboxInterrogatorTree dropboxFolderViewTree">
				{isLoading && (
					<>
						<div className="dropboxInterrogatorLoadingBarContainer">
							<LinearProgress variant="determinate" value={progressPercent} />
							<Typography className="dropboxInterrogatorTreeMessage">
								Scanning folders... {progressPercent}% ({progress.processed} processed, {progress.queued} queued)
							</Typography>
						</div>
						<Typography className="dropboxInterrogatorTreeMessage">
							Current: {progress.currentPath}{progress.dots}
						</Typography>
					</>
				)}

				{!isLoading && loadError && (
					<Typography className="dropboxInterrogatorTreeMessage" color="error">
						{loadError}
					</Typography>
				)}

				{!isLoading && !loadError && treeRows.length === 0 && (
					<Typography className="dropboxInterrogatorTreeMessage">No results.</Typography>
				)}

				{!isLoading && treeRows.length > 0 && (
					filteredTreeRows.length === 0 ? (
						<Typography className="dropboxInterrogatorTreeMessage">No results match the current filters.</Typography>
					) : (
						filteredTreeRows.map((row) => {
							// Most recently modified file in this folder (for folder-level summary)
							const newestFile = (row.fileNames || []).reduce((best, f) => {
								if (!f.modified) return best;
								if (!best || f.modified > best.modified) return f;
								return best;
							}, null);
							const folderLastEdited = newestFile?.modified
								? new Date(newestFile.modified).toLocaleDateString()
								: null;
							const folderLastEditor = newestFile?.modifiedById
								? (nameMap[newestFile.modifiedById] || null)
								: null;

							return (
								<Box
									key={`${row.fullPath}-${row.depth}`}
									className="dropboxInterrogatorTreeRow"
									sx={{ pl: `${row.depth * 2}ch` }}
								>
									<Typography className={row.selectedCount > 0 ? "dropboxInterrogatorHasFiles" : "dropboxInterrogatorEmpty"}>
										•{" "}
										<a
											href={buildDropboxFolderUrl(row.fullPath)}
											target="_blank"
											rel="noopener noreferrer"
											className="dropboxInterrogatorFolderLink"
										>
											{row.label}
										</a>
										{" "}({row.displaySummary})
										{folderLastEdited && (
											<span className="dropboxFolderViewMeta">
												{" — "}last edited {folderLastEdited}
												{folderLastEditor && ` by ${folderLastEditor}`}
											</span>
										)}
									</Typography>
									{fileVisibility === "show" && row.files.map((file) => {
										const modifiedDate = file.modified
											? new Date(file.modified).toLocaleDateString()
											: null;
										const modifiedBy = file.modifiedById
											? (nameMap[file.modifiedById] || null)
											: null;
										return (
											<Typography
												key={`${row.fullPath}-${file.fullPath}`}
												className="dropboxInterrogatorFileRow"
												sx={{ pl: `${(row.depth + 1) * 2}ch` }}
											>
												<DescriptionIcon fontSize="inherit" />
												{" "}
												<a
													href={buildDropboxPreviewUrl(file.fullPath)}
													target="_blank"
													rel="noopener noreferrer"
													className="dropboxInterrogatorFileLink"
												>
													{file.name}
												</a>
												{modifiedDate && (
													<span className="dropboxFolderViewMeta">
														{" — "}last edited {modifiedDate}
														{modifiedBy && ` by ${modifiedBy}`}
													</span>
												)}
											</Typography>
										);
									})}
								</Box>
							);
						})
					)
				)}
			</div>

			<p className="dropboxFolderViewFootnote">
				File counts are per folder (not recursive). Click a folder name to open in Dropbox.
			</p>
		</div>
	);
};

export default DropboxFolderView;
